import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '../services/auth';
import type { User, LoginCredentials, CreateUserData, UpdateUserData, ChangePasswordData } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  hasRole: (role: 'master' | 'admin' | 'user') => boolean;
  canManageUsers: () => boolean;
  canAccessMaster: () => boolean;
  getAllUsers: () => Promise<User[]>;
  createUser: (userData: CreateUserData) => Promise<{ success: boolean; error?: string }>;
  updateUser: (userData: UpdateUserData) => Promise<{ success: boolean; error?: string }>;
  deleteUser: (userId: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (passwordData: ChangePasswordData) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const result = await AuthService.login(credentials);
      
      // Verificar se o login foi bem-sucedido
      if (result.data && result.data.user) {
        // Converter dados do Supabase para formato User
        const user: User = {
          id: result.data.user.id,
          email: result.data.user.email,
          name: result.data.user.user_metadata?.name || result.data.user.email,
          role: result.data.user.user_metadata?.role || 'user',
          createdAt: result.data.user.created_at,
          updatedAt: result.data.user.updated_at,
          isActive: true
        };
        
        setUser(user);
        return { success: true, user };
      } else if (result.error) {
        // Retornar erro tratado
        return { success: false, error: result.error };
      } else {
        return { success: false, error: 'Erro desconhecido no login' };
      }
    } catch (error) {
      console.error('Erro no AuthContext login:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  const hasRole = (role: 'master' | 'admin' | 'user') => {
    return AuthService.hasRole(role);
  };

  const canManageUsers = () => {
    return AuthService.canManageUsers();
  };

  const canAccessMaster = () => {
    return AuthService.canAccessMaster();
  };

  const getAllUsers = async () => {
    return AuthService.getAllUsers();
  };

  const createUser = async (userData: CreateUserData) => {
    return AuthService.createUser(userData);
  };

  const updateUser = async (userData: UpdateUserData) => {
    return AuthService.updateUser(userData);
  };

  const deleteUser = async (userId: string) => {
    return AuthService.deleteUser(userId);
  };

  const changePassword = async (passwordData: ChangePasswordData) => {
    return AuthService.changePassword(passwordData);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
    canManageUsers,
    canAccessMaster,
    getAllUsers,
    createUser,
    updateUser,
    deleteUser,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
